import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import ArcaPageShell from './components/ArcaPageShell';
import { STATUS_KANBAN, PRIORIDADES, KANBAN_COLUNAS } from './utils/arcaFormConstants';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

const COLUNAS = KANBAN_COLUNAS.map((c) => c.status);

function UnidadeFilter({ value, onChange, unidades, isGlobal }) {
  return (
    <TextField
      select
      size="small"
      label="Unidade"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 200 }}
    >
      {isGlobal && <MenuItem value="TODAS">Todas as unidades</MenuItem>}
      {unidades.map((u) => (
        <MenuItem key={u.codigo} value={u.codigo}>
          {u.nome || u.codigo}
        </MenuItem>
      ))}
    </TextField>
  );
}

function statusChipColor(status) {
  const map = {
    BACKLOG: 'default',
    ABERTO: 'info',
    EM_ELABORACAO: 'default',
    EM_REVISAO: 'warning',
    EM_COTACAO: 'warning',
    PUBLICADO: 'info',
    EM_ANDAMENTO: 'warning',
    PRAZO_FINALIZADO: 'secondary',
    HOMOLOGADO: 'success',
    CANCELADO: 'error'
  };
  return map[status] || 'default';
}

function normalizeStatus(item) {
  const s = item.statusKanban || item.status_kanban || 'ABERTO';
  return COLUNAS.includes(s) ? s : 'ABERTO';
}

export default function ArcaEditaisPage() {
  const { unidadeSelecionada, isGlobalUser } = useAuth();
  const isGlobal = typeof isGlobalUser === 'function' ? isGlobalUser() : false;
  const [unidadeFiltro, setUnidadeFiltro] = useState(unidadeSelecionada?.codigo || '');
  const [unidades, setUnidades] = useState([]);
  const [editais, setEditais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [view, setView] = useState('kanban');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', prioridade: '' });
  const [docsDialog, setDocsDialog] = useState({
    open: false,
    item: null,
    loading: false,
    arquivos: [],
    error: ''
  });

  useEffect(() => {
    if (unidadeSelecionada?.codigo && !isGlobal) {
      setUnidadeFiltro(unidadeSelecionada.codigo);
    }
  }, [unidadeSelecionada?.codigo, isGlobal]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    // TODAS não é código de unidade — backend trata via resolveUnidadeFiltro
    if (unidadeFiltro && unidadeFiltro !== 'TODAS') p.set('unidade', unidadeFiltro);
    else if (unidadeFiltro === 'TODAS' && isGlobal) p.set('unidade', 'TODAS');
    return p.toString() ? `?${p.toString()}` : '';
  }, [unidadeFiltro, isGlobal]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rU, rE] = await Promise.all([
        apiFetch('/api/arca/unidades'),
        apiFetch(`/api/arca/editais${qs}`)
      ]);
      if (rU.ok) setUnidades(await rU.json());
      if (rE.ok) setEditais(await rE.json());
      else {
        const err = await rE.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao carregar editais');
      }
    } catch (e) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const porColuna = useMemo(() => {
    const map = Object.fromEntries(COLUNAS.map((c) => [c, []]));
    editais.forEach((e) => {
      map[normalizeStatus(e)].push(e);
    });
    return map;
  }, [editais]);

  const abrirEdit = (item) => {
    setEditItem(item);
    setEditForm({
      status: normalizeStatus(item),
      prioridade: item.prioridade || 'MEDIA'
    });
  };

  const patchMover = async (item, status, prioridade) => {
    const res = await apiFetch(`/api/arca/editais/${item.id}/mover`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        status,
        origem: item.origem || 'gav',
        prioridade: prioridade || undefined
      })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao atualizar');
  };

  const salvarStatus = async () => {
    if (!editItem) return;
    try {
      await patchMover(editItem, editForm.status, editForm.prioridade);
      setEditItem(null);
      setSnack('Edital atualizado');
      carregar();
    } catch (e) {
      setError(e.message);
    }
  };

  const moverColuna = async (item, novoStatus) => {
    try {
      await patchMover(item, novoStatus, item.prioridade);
      carregar();
    } catch (e) {
      setError(e.message);
    }
  };

  const abrirDocumentosConsolidados = async (item) => {
    if (!item?.processoId) {
      setDocsDialog({
        open: true,
        item,
        loading: false,
        arquivos: [],
        error: 'Este edital ainda não está vinculado a um processo de contratação no SGC.'
      });
      return;
    }
    setDocsDialog({ open: true, item, loading: true, arquivos: [], error: '' });
    try {
      const res = await apiFetch(`/api/contratos/processos/${item.processoId}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar documentos do processo');
      const arquivos = (data.arquivos || [])
        .filter((a) => a.tipo === 'BUNDLE' || a.tipo === 'FINAL')
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      setDocsDialog({
        open: true,
        item,
        loading: false,
        arquivos,
        error: arquivos.length ? '' : 'Nenhum consolidado gerado ainda neste processo.'
      });
    } catch (e) {
      setDocsDialog({
        open: true,
        item,
        loading: false,
        arquivos: [],
        error: e.message || 'Erro ao carregar documentos'
      });
    }
  };

  const EditalCard = ({ item }) => (
    <Card variant="outlined" sx={{ mb: 1.5, bgcolor: 'background.paper' }}>
      <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {item.titulo || item.codigo || `Edital #${item.id}`}
          </Typography>
          {item.prioridade && (
            <Chip size="small" label={item.prioridade} variant="outlined" sx={{ height: 22 }} />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block">
          {item.codigo || '—'}
          {item.unidade ? ` · ${item.unidade}` : ''}
        </Typography>
        {item.origem && (
          <Chip size="small" label={item.origem} sx={{ mt: 0.75, height: 20, fontSize: 10 }} />
        )}
        {item.dataEncerramento && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Encerramento: {String(item.dataEncerramento).slice(0, 10)}
          </Typography>
        )}
        {Number(item.consolidadosCount) > 0 && (
          <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
            {item.consolidadosCount} documento(s) consolidado(s)
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, px: 1, pb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Button size="small" onClick={() => abrirEdit(item)}>
          Status
        </Button>
        <Button
          size="small"
          startIcon={<FolderOpenIcon fontSize="small" />}
          onClick={() => abrirDocumentosConsolidados(item)}
          disabled={!item.processoId}
          sx={{ textTransform: 'none' }}
        >
          Consolidados
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <ArcaPageShell
      title="Editais"
      subtitle="Visão unificada dos editais do SGC (GAV + portal), com status estilo kanban e filtro por unidade."
      maxWidth={1600}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <UnidadeFilter
          value={unidadeFiltro || (isGlobal ? 'TODAS' : '')}
          onChange={setUnidadeFiltro}
          unidades={
            unidades.length
              ? unidades
              : unidadeSelecionada
                ? [{ codigo: unidadeSelecionada.codigo, nome: unidadeSelecionada.nome }]
                : []
          }
          isGlobal={isGlobal}
        />
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="kanban">
            <ViewKanbanIcon fontSize="small" sx={{ mr: 0.5 }} /> Kanban
          </ToggleButton>
          <ToggleButton value="lista">
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> Lista
          </ToggleButton>
        </ToggleButtonGroup>
        <Button startIcon={<RefreshIcon />} onClick={carregar}>
          Atualizar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {editais.length} edital(is)
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {snack && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSnack('')}>
          {snack}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : view === 'kanban' ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'start',
            overflowX: 'auto',
            pb: 1
          }}
        >
          {COLUNAS.map((col) => (
            <Paper
              key={col}
              variant="outlined"
              sx={{ p: 1.5, minHeight: 280, minWidth: 210, flex: '0 0 210px', bgcolor: 'action.hover' }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
                  {STATUS_KANBAN.find((s) => s.value === col)?.label || col}
                </Typography>
                <Chip size="small" label={porColuna[col]?.length || 0} />
              </Stack>
              {(porColuna[col] || []).map((item) => (
                <EditalCard key={`${item.origem}-${item.id}`} item={item} />
              ))}
              {(porColuna[col] || []).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  Nenhum edital
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      ) : (
        <Grid container spacing={1.5}>
          {editais.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={`${item.origem}-${item.id}`}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle1" fontWeight={700}>
                    {item.titulo || item.codigo}
                  </Typography>
                  <Chip
                    size="small"
                    label={normalizeStatus(item)}
                    color={statusChipColor(normalizeStatus(item))}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {item.codigo} · {item.unidade || '—'} · {item.origem || 'gav'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" onClick={() => abrirEdit(item)}>
                    Alterar status
                  </Button>
                  <Button
                    size="small"
                    startIcon={<FolderOpenIcon fontSize="small" />}
                    onClick={() => abrirDocumentosConsolidados(item)}
                    disabled={!item.processoId}
                    sx={{ textTransform: 'none' }}
                  >
                    Consolidados
                  </Button>
                  {COLUNAS.filter((c) => c !== normalizeStatus(item))
                    .slice(0, 2)
                    .map((c) => (
                      <Button key={c} size="small" onClick={() => moverColuna(item, c)}>
                        → {STATUS_KANBAN.find((s) => s.value === c)?.label || c}
                      </Button>
                    ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
          {editais.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">Nenhum edital encontrado para o filtro atual.</Alert>
            </Grid>
          )}
        </Grid>
      )}

      <DialogStatus
        open={Boolean(editItem)}
        item={editItem}
        form={editForm}
        setForm={setEditForm}
        onClose={() => setEditItem(null)}
        onSave={salvarStatus}
      />

      <Dialog
        open={docsDialog.open}
        onClose={() => setDocsDialog({ open: false, item: null, loading: false, arquivos: [], error: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Documentos consolidados</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {docsDialog.item?.titulo || docsDialog.item?.codigo || 'Edital'}
            {docsDialog.item?.processoId ? ` · Processo #${docsDialog.item.processoId}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Atualiza conforme o processo de contratação avança no SGC (bundles por etapa e documento final).
          </Typography>
          {docsDialog.loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : docsDialog.error && !docsDialog.arquivos.length ? (
            <Alert severity="info">{docsDialog.error}</Alert>
          ) : (
            <Stack spacing={0.75}>
              {docsDialog.arquivos.map((arquivo) => {
                const url = arquivo.url || arquivo.arquivoUrl || arquivo.arquivoPath;
                const rotulo =
                  arquivo.tipo === 'FINAL'
                    ? 'Documento final consolidado'
                    : `Consolidado etapa ${arquivo.etapaOrdem || arquivo.etapaNome || arquivo.etapaId || ''}`;
                return (
                  <Button
                    key={arquivo.id}
                    size="small"
                    variant="outlined"
                    startIcon={
                      arquivo.tipo === 'FINAL' ? (
                        <PictureAsPdfIcon fontSize="small" />
                      ) : (
                        <DescriptionOutlinedIcon fontSize="small" />
                      )
                    }
                    onClick={() => openAuthenticatedUpload(url)}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', borderRadius: 2 }}
                  >
                    {rotulo}
                    {arquivo.nome ? ` — ${arquivo.nome}` : ''}
                  </Button>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDocsDialog({ open: false, item: null, loading: false, arquivos: [], error: '' })}
            sx={{ textTransform: 'none' }}
          >
            Fechar
          </Button>
          {docsDialog.item?.processoId && (
            <Button
              onClick={() => abrirDocumentosConsolidados(docsDialog.item)}
              sx={{ textTransform: 'none' }}
            >
              Atualizar lista
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ArcaPageShell>
  );
}

function DialogStatus({ open, item, form, setForm, onClose, onSave }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Atualizar edital</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {item?.titulo || item?.codigo}
            {item?.origem === 'portal' ? ' (portal — só ativo/cancelado)' : ''}
          </Typography>
          <TextField
            select
            label="Status kanban"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            fullWidth
          >
            {STATUS_KANBAN.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Prioridade"
            value={form.prioridade}
            onChange={(e) => setForm((f) => ({ ...f, prioridade: e.target.value }))}
            fullWidth
            disabled={item?.origem === 'portal'}
          >
            {PRIORIDADES.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={onSave}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
